import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { PageLayout } from '../../components/common/PageLayout'
import { Section, Button, StatCard } from '../../components/common/DesignComponents'
import { TabNavigation, Breadcrumb } from '../../components/common/NavigationComponents'
import { RankItem, Alert } from '../../components/common/LayoutPatterns'

interface LeaderboardEntry {
  rank: number
  childId: string
  childName: string
  userName: string
  level: number
  totalXP: number
  stage: string
  happiness?: number
}

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[]
  offset: number
  limit: number
  total: number
}

type SortBy = 'level' | 'xp'
type LeaderboardType = 'global' | 'stage' | 'trending' | 'friends'

const CHILD_STAGES = ['BABY', 'TODDLER', 'KID', 'PRETEEN', 'TEEN']

export const LeaderboardPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>()
  const navigate = useNavigate()

  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [leaderboardType, setLeaderboardType] = useState<LeaderboardType>('global')
  const [sortBy, setSortBy] = useState<SortBy>('level')
  const [selectedStage, setSelectedStage] = useState<string>('BABY')
  const [page, setPage] = useState(0)

  const [userRank, setUserRank] = useState<any>(null)
  const [showUserRank, setShowUserRank] = useState(false)

  useEffect(() => {
    fetchLeaderboard()
  }, [leaderboardType, sortBy, selectedStage, page])

  const fetchLeaderboard = async () => {
    try {
      setLoading(true)
      let url = ''

      if (leaderboardType === 'global') {
        url = `/api/leaderboard?sortBy=${sortBy}&limit=100&offset=${page * 100}`
      } else if (leaderboardType === 'stage') {
        url = `/api/leaderboard/stage/${selectedStage}?limit=100&offset=${page * 100}`
      } else if (leaderboardType === 'trending') {
        url = `/api/leaderboard/trending?limit=100`
      } else if (leaderboardType === 'friends') {
        url = `/api/leaderboard/friends`
      }

      const response = await axiosClient.get(url)
      if (response.data.success) {
        setEntries(response.data.data.leaderboard)
      }
    } catch (err) {
      setError('Failed to load leaderboard')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserRank = async () => {
    try {
      if (!childId) return
      const response = await axiosClient.get(`/api/children/${childId}/leaderboard/rank`)
      if (response.data.success) {
        setUserRank(response.data.data)
        setShowUserRank(true)
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (childId) {
      fetchUserRank()
    }
  }, [childId])

  if (loading && entries.length === 0) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin text-5xl mb-4">🏆</div>
            <p className="text-purple-600 font-semibold">Loading leaderboard...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <Section>
        {/* Header */}
        <Breadcrumb
          items={[
            { label: 'Children', href: '/children' },
            { label: 'Leaderboard', current: true },
          ]}
          className="mb-6"
        />

        <div className="mb-8">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
            🏆 Leaderboard
          </h1>
          <p className="text-gray-600">See how your child ranks against others!</p>
        </div>

        {/* Leaderboard Type Selector */}
        <div className="mb-6">
          <TabNavigation
            tabs={[
              { label: 'Global', value: 'global', emoji: '🌍' },
              { label: 'By Stage', value: 'stage', emoji: '📊' },
              { label: 'Trending', value: 'trending', emoji: '🔥' },
              { label: 'My Children', value: 'friends', emoji: '👨‍👧' },
            ]}
            activeTab={leaderboardType}
            onTabChange={(tab) => {
              setLeaderboardType(tab as LeaderboardType)
              setPage(0)
            }}
          />
        </div>

        {/* Filters */}
        {leaderboardType === 'stage' && (
          <div className="mb-6 flex flex-wrap gap-2">
            {CHILD_STAGES.map((stage) => (
              <button
                key={stage}
                onClick={() => {
                  setSelectedStage(stage)
                  setPage(0)
                }}
                className={`px-4 py-2 rounded-full text-sm font-bold transition ${
                  selectedStage === stage
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {stage}
              </button>
            ))}
          </div>
        )}

        {leaderboardType === 'global' && (
          <div className="mb-6 flex gap-2">
            {(['level', 'xp'] as const).map((sort) => (
              <button
                key={sort}
                onClick={() => setSortBy(sort)}
                className={`px-4 py-2 rounded-lg font-bold transition ${
                  sortBy === sort
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {sort === 'level' ? '📊 Level' : '⭐ Experience'}
              </button>
            ))}
          </div>
        )}

        {/* Your Rank Section */}
        {showUserRank && userRank && (
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200 p-6 mb-6">
            <h3 className="font-bold text-orange-600 mb-4">Your Rank</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Global Rank"
                value={`#${userRank.globalRank}`}
                icon="🌍"
                className="bg-white"
              />
              <StatCard
                label="Stage Rank"
                value={`#${userRank.stageRank}`}
                icon="📊"
                className="bg-white"
              />
              <StatCard
                label="Level"
                value={userRank.level.toString()}
                icon="📈"
                className="bg-white"
              />
              <StatCard
                label="Total XP"
                value={userRank.totalXP.toString()}
                icon="⭐"
                className="bg-white"
              />
            </div>
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="overflow-x-auto bg-white rounded-xl border-2 border-purple-200 mb-6">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-bold">Rank</th>
                <th className="px-6 py-3 text-left text-sm font-bold">Child Name</th>
                <th className="px-6 py-3 text-left text-sm font-bold">Parent</th>
                <th className="px-6 py-3 text-center text-sm font-bold">Level</th>
                <th className="px-6 py-3 text-center text-sm font-bold">Experience</th>
                {leaderboardType !== 'stage' && (
                  <th className="px-6 py-3 text-left text-sm font-bold">Stage</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-purple-100">
              {entries.map((entry, index) => (
                <tr
                  key={`${entry.rank}-${entry.childId}`}
                  className={`hover:bg-purple-50 transition cursor-pointer ${
                    entry.childId === childId ? 'bg-purple-100' : ''
                  }`}
                >
                  <td className="px-6 py-4 text-left">
                    <div className="flex items-center gap-2 font-bold">
                      {entry.rank === 1 && <span className="text-2xl">🥇</span>}
                      {entry.rank === 2 && <span className="text-2xl">🥈</span>}
                      {entry.rank === 3 && <span className="text-2xl">🥉</span>}
                      {entry.rank > 3 && (
                        <span className="text-lg text-purple-600">#{entry.rank}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <p className="font-bold text-purple-600">{entry.childName}</p>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <p className="text-gray-600 text-sm">{entry.userName}</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-bold">
                      {entry.level}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <p className="font-bold text-blue-600">{entry.totalXP}</p>
                  </td>
                  {leaderboardType !== 'stage' && (
                    <td className="px-6 py-4 text-left">
                      <span className="text-xs font-bold bg-purple-100 px-2 py-1 rounded-full text-purple-600">
                        {entry.stage}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {entries.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">📭 No entries found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {(leaderboardType === 'global' || leaderboardType === 'stage') && (
          <div className="flex justify-center gap-3 mb-6">
            <Button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className={page === 0 ? 'bg-gray-300' : 'bg-purple-600 hover:bg-purple-700'}
            >
              ◀ Previous
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-purple-600 font-bold">Page {page + 1}</p>
            </div>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={entries.length < 100}
              className={entries.length < 100 ? 'bg-gray-300' : 'bg-purple-600 hover:bg-purple-700'}
            >
              Next ▶
            </Button>
          </div>
        )}

        {error && (
          <Alert
            type="error"
            title="Error"
            message={error}
            icon="❌"
          />
        )}
      </Section>
    </PageLayout>
  )
}

export default LeaderboardPage
